/**
 * DELIVERY TIMES — a company-defined menu of turnaround options (9/17/26).
 *
 * `.server/tenant-setup/delivery.ts` worked out the promise maths for a
 * fixed three-rung ladder, but nothing ever offered it to a customer and
 * a company could not name its own rungs. This is the menu a company
 * actually sells from:
 *
 *   - It is an OPTION. `offered` is the company's switch; absent/false
 *     means customers are never asked and every order is standard.
 *   - As many options as the company wants (capped at 4 so the ordering
 *     conversation stays sane), each with its OWN NAME, a fee, and a
 *     promise: N days after the shoot, by a local hour. Days are CALENDAR
 *     days counted from the shoot day, whatever day of the week that is,
 *     unless the company turns on "business days only".
 *   - SEEDED, not blank: the first time a company opens the page the
 *     founder's ladder is pre-filled, so turning it on is "review three
 *     rows and press Save", not a data-entry project.
 *   - The fee is SNAPSHOTTED onto the order when it is chosen
 *     (`order.data.delivery_choice`), so changing the menu later never
 *     re-prices an order a customer already placed.
 *
 * Pure and client-safe: the settings form, the quote endpoint, the
 * intake and the order model all read the same functions.
 */
import { isoMillisOrNull, isoOrNull } from '~/lib/safe-date';
import { DateTime } from 'luxon';
import { z } from 'zod';

export const DELIVERY_MENU_MAX_OPTIONS = 4;

export const DeliveryMenuOptionSchema = z.object({
  /** Stable slug — what an order records. Derived from the name on first save. */
  id: z.string().min(1).max(40),
  /** The company's own words: "Rush", "Next Morning", "Weekend Warrior". */
  name: z.string().min(1).max(60),
  /** Dollars added to the order. 0 = included. */
  fee: z.number().nonnegative(),
  /** Days after the shoot the work is promised. 0 = the same day. */
  days_after: z.number().int().min(0).max(14),
  /** Local hour (0–23) it is promised by. */
  ready_hour: z.number().int().min(0).max(23),
  /**
   * Absent = offered. Lets a company park an option without deleting it.
   * A stored null (an older save wrote one) reads as absent.
   */
  enabled: z
    .boolean()
    .nullish()
    .transform((value) => value ?? undefined),
});

export type DeliveryMenuOption = z.infer<typeof DeliveryMenuOptionSchema>;

export const DeliveryMenuSchema = z.object({
  /** THE company switch. Absent/false = customers are never asked. */
  offered: z.boolean().optional(),
  /**
   * Skip weekends when counting days. Default FALSE (founder, 2 Oct 2026:
   * "any shoot no matter what day could participate in the delivery options,
   * they are relative to the day they are shot"). A Saturday shoot with
   * "Next morning" is promised Sunday morning. A company that does not work
   * weekends turns this on, and then a Friday shoot's next day is Monday.
   */
  business_days_only: z.boolean().optional(),
  options: z.array(DeliveryMenuOptionSchema).max(DELIVERY_MENU_MAX_OPTIONS).optional(),
  /**
   * SCHEDULED DELIVERY (2 Oct 2026). The company keeps its promise to the
   * minute: every order placed while this is on carries the promise it was
   * sold (`order.data.delivery_release`), the office and the photographer see
   * when the photos are due, and the photos are released to the customer at
   * the promised time, never before. Independent of `offered`: a company with
   * only its standard turnaround can still schedule it.
   *
   * Absent/false = exactly as before: nothing is stamped on an order and
   * media goes out the moment it is delivered.
   */
  scheduled: z.boolean().optional(),
  /**
   * When `scheduled` was switched on. Only orders created at or after it are
   * stamped: those customers were shown the promise when they ordered.
   */
  scheduled_since: z.string().optional(),
  /**
   * SALES TAX ON THE SPEED (founder, 5 Oct 2026): whether what a customer
   * pays for a faster delivery time is taxed. Absent/false = exactly as
   * before: the "Delivery: …" line carries no tax. On: that line is taxed,
   * and so is the speed an offering's price carries (Express), on a line of
   * its own (catalog-order-plan.ts catalogPlanDelivery). Snapshotted onto
   * each order with its choice (`DeliveryChoice.taxable`), so switching it
   * later re-taxes no order already placed.
   */
  speed_taxable: z.boolean().optional(),
});

export type DeliveryMenu = z.infer<typeof DeliveryMenuSchema>;

/** What an order remembers about the choice. `fee` is a decimal string, like every money field on an order. */
export const DeliveryChoiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  fee: z.string(),
  days_after: z.number().optional(),
  ready_hour: z.number().optional(),
  /**
   * The speed's price when it is INSIDE the price of what was ordered rather
   * than a line of its own: an "Express" offering linked to this option
   * (CatalogProductData.delivery_option_id). `fee` is then "0.00", so the
   * order gains no "Delivery" line, and this is what the customer paid for
   * the speed: what a late delivery credit gives back.
   */
  included: z.string().optional(),
  /** The company taxed delivery speed when this was sold (`DeliveryMenu.speed_taxable`): the "Delivery: …" line is taxed. */
  taxable: z.boolean().optional(),
  /**
   * The `included` speed was taken out of the offering's untaxed package
   * line and invoiced on a taxed line of its own ("Delivery: Next morning
   * (included)"). Only where the order carries such a line (a company that
   * invoices catalog prices); elsewhere the included speed stays inside the
   * offering's price, taxed as that price is.
   */
  included_taxable: z.boolean().optional(),
});

export type DeliveryChoice = z.infer<typeof DeliveryChoiceSchema>;

/** The part of an option that says WHEN: N days after the shoot day, by a local hour. */
export interface DeliveryRule {
  days_after: number;
  ready_hour: number;
  /** Absent = calendar days (see DeliveryMenuSchema.business_days_only). */
  business_days_only?: boolean;
}

/**
 * THE PROMISE AN ORDER CARRIES (`order.data.delivery_release`), stamped when
 * the order is placed while the company's `scheduled` switch is on.
 *
 * A snapshot of the option as sold, so that nothing the company changes
 * afterwards (the menu, the switch itself, the Company page) moves or drops
 * an order's promise. Everything about scheduled delivery reads this stamp
 * and nothing else: an order without it is delivered exactly as before.
 */
export interface DeliveryRelease extends DeliveryRule {
  option_id: string;
  option_name: string;
  /**
   * What the customer paid for the speed, as a decimal string: the option's
   * fee, or for an offering whose price carries the speed, the fee of the
   * option it is linked to. "0.00" for the standard turnaround.
   */
  paid: string;
  stamped_at: string;
  /**
   * The promised instant, counted from the shoot. Kept in step with the
   * appointment while the shoot can still move, and left alone once it has
   * happened (`frozen_at`). Absent while no shoot is scheduled, and for a
   * promise that cannot be kept (a same-day hour already past when the shoot
   * ends): such an order has no time to be held to.
   */
  promised_at?: string;
  /** The appointment `promised_at` was counted from. */
  shoot_appointment_id?: string;
  /** Set once the shoot has happened: `promised_at` no longer follows the appointment. */
  frozen_at?: string;
  /**
   * What `promised_at` was counted from while the order has NO shoot on the
   * calendar (an order no provider was booked to; a shoot taken off it):
   * the day the customer asked for, else when its media first came in
   * (.server/delivery-promise.ts deliveryAnchor). Absent while it counts
   * from the shoot.
   */
  anchor?: 'requested' | 'upload' | 'delivery';
  /** For an `upload` or `delivery` anchor: when that was, kept so it never moves. */
  anchor_at?: string;
  /**
   * Whether the speed's price was taxed when it was sold: a late credit takes
   * the same tax back off. A menu option's fee line is not taxed; an offering
   * whose price carries the speed says so itself.
   */
  paid_taxable?: boolean;

  // ---- SCHEDULED DELIVERY: the hold and the release (.server/delivery-release.ts)

  /** The instant an order pass has been asked for, to release at the promised time. */
  scheduled_for?: string;
  /**
   * When the order's media was released to the customer. The ONE fact the
   * hold reads: an order with a promised time and no `released_at` is held.
   * Written once, by whoever releases first (the pass at the promised time,
   * or the office's "Release now").
   */
  released_at?: string;
  /** 'auto' for the pass; an office user's id for "Release now". */
  released_by?: string;
  /** That office user's name, as it was then. */
  released_by_name?: string;
  /** How the release sat against the promise. */
  release?: DeliveryReleaseHow;
  /** Whole minutes past `promised_at`, for a late release. */
  late_minutes?: number;
  /**
   * LATE CREDIT: what was taken off the invoice because a paid-for speed was
   * delivered late (`paid`), as a decimal string. The order model turns it
   * into a "Late delivery credit" line. Written with `released_at`, in the
   * same write, so one release can never credit twice.
   */
  credit?: string;
  /**
   * The order had already been paid when the credit landed, so the credit
   * left this much to give back (the invoice's negative balance, tax
   * included). The office refunds it; nothing here moves money.
   */
  refund_owed?: string;
}

export type DeliveryReleaseHow = 'early' | 'on_time' | 'late';

/**
 * What the order MODELS are given of the stamp (TenantOrderContext): the
 * promise and the credit, which decide the "Delivery" row, the rush tag, the
 * due time and the invoice line. Not the release's paperwork (who released
 * it, what was scheduled): a context is sent to pages, the customer's too.
 */
export type DeliveryReleaseContext = Pick<
  DeliveryRelease,
  | 'option_id'
  | 'option_name'
  | 'days_after'
  | 'ready_hour'
  | 'business_days_only'
  | 'paid'
  | 'paid_taxable'
  | 'promised_at'
  | 'shoot_appointment_id'
  | 'credit'
>;

export function deliveryReleaseContext(release: DeliveryRelease | undefined): DeliveryReleaseContext | undefined {
  if (!release) {
    return undefined;
  }

  return {
    option_id: release.option_id,
    option_name: release.option_name,
    days_after: release.days_after,
    ready_hour: release.ready_hour,
    business_days_only: release.business_days_only,
    paid: release.paid,
    ...(release.paid_taxable ? { paid_taxable: true } : {}),
    ...(release.promised_at ? { promised_at: release.promised_at } : {}),
    ...(release.shoot_appointment_id ? { shoot_appointment_id: release.shoot_appointment_id } : {}),
    ...(release.credit ? { credit: release.credit } : {}),
  };
}

/**
 * How long past the promised time a release still counts as on time. The
 * release is a pass over the order asked for at the promised instant; it
 * takes seconds, and a retry a few minutes. One number, here.
 */
export const DELIVERY_LATE_GRACE_MINUTES = 15;

/**
 * THE HOLD, in one line: an order that carries a promise with a time, and has
 * not been released. No clock in it: the release is a recorded event, written
 * by the same pass that charges the card and tells the customer, so no page
 * can open a second before that pass has run.
 */
export function deliveryIsHeld(release: Pick<DeliveryRelease, 'promised_at' | 'released_at'> | undefined): boolean {
  return Boolean(release?.promised_at) && !release?.released_at;
}

export interface DeliveryReleaseOutcome {
  release: DeliveryReleaseHow;
  /** Whole minutes late; 0 unless `release` is 'late'. */
  late_minutes: number;
  /** The credit a late release owes the customer, as a decimal string; undefined when none. */
  credit?: string;
}

/**
 * What a release at `nowMs` is, against the order's promise.
 *
 *   - before the promised time: EARLY (only the office can do that);
 *   - up to the grace after it: ON TIME;
 *   - later than that: LATE, by whole minutes from the promised time, and
 *     when the customer paid for the speed, a credit of exactly that.
 *
 * An order with no promised time (no shoot was ever scheduled, or a same-day
 * hour that had already passed) has nothing to be late against: on time.
 */
export function deliveryReleaseOutcome(
  release: Pick<DeliveryRelease, 'promised_at' | 'paid'>,
  nowMs: number,
): DeliveryReleaseOutcome {
  const promised = release.promised_at ? Date.parse(release.promised_at) : Number.NaN;

  if (!Number.isFinite(promised)) {
    return { release: 'on_time', late_minutes: 0 };
  }

  if (nowMs < promised) {
    return { release: 'early', late_minutes: 0 };
  }

  if (nowMs <= promised + DELIVERY_LATE_GRACE_MINUTES * 60_000) {
    return { release: 'on_time', late_minutes: 0 };
  }

  const paid = Number(release.paid);

  return {
    release: 'late',
    late_minutes: Math.floor((nowMs - promised) / 60_000),
    ...(Number.isFinite(paid) && paid > 0 ? { credit: paid.toFixed(2) } : {}),
  };
}

/** The invoice line a late credit becomes: "Late delivery credit — Next morning". */
export function deliveryCreditDescription(optionName: string): string {
  return `Late delivery credit — ${optionName}`;
}

/**
 * The option a customer gets when they pick nothing: the cheapest one still
 * offered (the settings page requires a $0 option before the menu can be
 * offered). Read from the stored rows whether or not the menu is `offered`,
 * because a company may schedule its standard turnaround without selling
 * faster ones.
 */
export function deliveryStandardOption(menu: DeliveryMenu): DeliveryMenuOption | undefined {
  const live = (menu.options ?? []).filter((o) => o.enabled !== false);

  return [...live].sort((a, b) => a.fee - b.fee)[0];
}

/** The founder's ladder — pre-filled the first time a company opens the page. */
export const DELIVERY_MENU_SEED: DeliveryMenuOption[] = [
  { id: 'standard', name: 'Standard — end of day tomorrow', fee: 0, days_after: 1, ready_hour: 17 },
  { id: 'next_morning', name: 'Next morning', fee: 50, days_after: 1, ready_hour: 9 },
  { id: 'same_day', name: 'Same day', fee: 100, days_after: 0, ready_hour: 23 },
];

/** Tolerant read of `company.delivery` (typed loosely in the tenant schema). */
/**
 * The company's delivery menu as stored. NEVER all-or-nothing: a menu that
 * fails as a whole (one bad option row) keeps every switch and every row
 * that is good. Reading it as `{}` turned delivery times AND the scheduled
 * hold OFF on the settings page, so the next save switched them off for
 * real, and no order was stamped meanwhile (staging, 5 Oct 2026).
 */
export function parseDeliveryMenu(
  raw: unknown,
  /**
   * Log what did not parse. Only where the menu is SAVED (Settings →
   * Delivery Times): every order pass, price check and page reads it, and a
   * warning on each read would bury the logs (review, 5 Oct 2026).
   */
  opts: { warn?: boolean } = {},
): DeliveryMenu {
  const parsed = DeliveryMenuSchema.safeParse(raw ?? {});

  if (parsed.success) {
    return parsed.data;
  }

  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const { options, ...rest } = raw as Record<string, unknown>;
  const shape = DeliveryMenuSchema.shape as Record<string, z.ZodTypeAny>;
  const top: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(rest)) {
    const field = key === 'options' ? undefined : shape[key]?.safeParse(value);

    if (field?.success && field.data !== undefined) {
      top[key] = field.data;
    }
  }

  const rows = Array.isArray(options)
    ? options.flatMap((row) => {
        const one = DeliveryMenuOptionSchema.safeParse(row);

        return one.success ? [one.data] : [];
      })
    : undefined;

  if (opts.warn) {
    console.warn('parseDeliveryMenu: the stored menu did not parse as a whole; its good parts were kept', parsed.error.issues.slice(0, 3));
  }

  return { ...(top as DeliveryMenu), ...(rows ? { options: rows.slice(0, DELIVERY_MENU_MAX_OPTIONS) } : {}) };
}

/** The rows the settings page shows: what is stored, or the seed when nothing is. */
export function deliveryMenuRows(menu: DeliveryMenu): DeliveryMenuOption[] {
  return menu.options && menu.options.length > 0 ? menu.options : DELIVERY_MENU_SEED;
}

export function deliverySlug(name: string, taken: Set<string>): string {
  const base =
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 32) || 'option';

  let slug = base;
  let n = 2;

  while (taken.has(slug)) {
    slug = `${base}_${n++}`;
  }

  taken.add(slug);

  return slug;
}

export type DeliveryMenuSave = { ok: true; menu: DeliveryMenu } | { ok: false; problems: string[] };

/**
 * The menu a save of the Delivery Times page stores, or what is wrong with it.
 *
 * `menu` names every switch, with `undefined` for one that is off: the page
 * stores it with a merge patch, where an absent key is KEPT and an
 * `undefined` one is removed. A switch left out when unticked would stay on.
 *
 * `scheduled_since` is stamped the moment scheduled delivery goes on, kept
 * while it stays on, and cleared when it goes off: only orders created after
 * it are scheduled, and switching it off and on again starts from then.
 */
export function deliveryMenuFromForm(
  form: FormData,
  existing: DeliveryMenu,
  { now, scheduledAvailable }: { now: Date; scheduledAvailable: boolean },
): DeliveryMenuSave {
  const names = form.getAll('name').map(String);
  const ids = form.getAll('id').map(String);
  const fees = form.getAll('fee').map(String);
  const days = form.getAll('days_after').map(String);
  const hours = form.getAll('ready_hour').map(String);
  const states = form.getAll('state').map(String);

  const taken = new Set(ids.filter((id) => id.trim() !== ''));
  const options: DeliveryMenuOption[] = [];
  const problems: string[] = [];

  names.forEach((raw, i) => {
    const name = raw.trim();

    // A blank name is how a row is removed.
    if (!name) {
      return;
    }

    const fee = Number(fees[i] || '0');
    const daysAfter = Number(days[i] || '0');
    const readyHour = Number(hours[i] || '17');

    if (!Number.isFinite(fee) || fee < 0) {
      problems.push(`"${name}": the fee must be zero or more.`);
      return;
    }

    options.push({
      id: ids[i]?.trim() || deliverySlug(name, taken),
      name,
      fee: Math.round(fee * 100) / 100,
      days_after: Math.max(0, Math.min(14, Math.trunc(daysAfter))),
      ready_hour: Math.max(0, Math.min(23, Math.trunc(readyHour))),
      // Only a PAUSED row says so. An `enabled: undefined` key inside the
      // options array is written as null (merge-patch replaces arrays whole
      // and does not delete inside them), and a null failed the schema, so
      // the whole menu read back as nothing (staging, 5 Oct 2026).
      ...(states[i] === 'paused' ? { enabled: false } : {}),
    });
  });

  const offered = form.get('offered') === 'on';
  // A company that cannot schedule (its own order form sells its speeds)
  // cannot turn it on by posting the field.
  const scheduled = scheduledAvailable && form.get('scheduled') === 'on';
  const live = options.filter((o) => o.enabled !== false);

  if (options.length > DELIVERY_MENU_MAX_OPTIONS) {
    problems.push(`Keep it to ${DELIVERY_MENU_MAX_OPTIONS} options — more than that is too long to read to a customer.`);
  }

  if (offered && live.length < 2) {
    problems.push('To offer delivery times you need at least two live options — one choice is not a choice.');
  }

  if ((offered || scheduled) && !live.some((o) => o.fee === 0)) {
    problems.push('Include one option with a $0 fee. That is your standard turnaround — what a customer gets if they pick nothing.');
  }

  if (problems.length > 0) {
    return { ok: false, problems };
  }

  const parsed = DeliveryMenuSchema.parse({
    business_days_only: form.get('business_days_only') === 'on',
    options,
  });

  return {
    ok: true,
    menu: {
      ...parsed,
      offered: offered ? true : undefined,
      scheduled: scheduled ? true : undefined,
      // Off is `undefined` (removed by the merge patch): today's untaxed delivery line.
      speed_taxable: form.get('speed_taxable') === 'on' ? true : undefined,
      scheduled_since: scheduled
        ? (existing.scheduled === true && existing.scheduled_since) || now.toISOString()
        : undefined,
    },
  };
}

/** Options a customer may be offered — empty unless the company switched the menu on. */
export function deliveryMenuOffered(menu: DeliveryMenu): DeliveryMenuOption[] {
  if (!menu.offered) {
    return [];
  }

  const live = (menu.options ?? []).filter((o) => o.enabled !== false);

  // A menu of one is not a choice; asking would only slow the order down.
  return live.length >= 2 ? live : [];
}

export function deliveryMenuFind(menu: DeliveryMenu, id: string | null | undefined): DeliveryMenuOption | undefined {
  if (!id) {
    return undefined;
  }

  return deliveryMenuOffered(menu).find((o) => o.id === id);
}

export function deliveryChoiceOf(option: DeliveryMenuOption, menu?: Pick<DeliveryMenu, 'speed_taxable'>): DeliveryChoice {
  return {
    id: option.id,
    name: option.name,
    fee: option.fee.toFixed(2),
    days_after: option.days_after,
    ready_hour: option.ready_hour,
    // Taxed as the company sold speed at the time (DeliveryMenu.speed_taxable). Absent: untaxed, as before.
    ...(menu?.speed_taxable ? { taxable: true } : {}),
  };
}

function hourSpoken(hour: number): string {
  if (hour === 0) return 'midnight';
  if (hour === 12) return 'noon';

  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

/**
 * The promise in words when the shoot time is not known yet (quote time):
 * "the same day as your shoot, by 11pm" / "the next business day, by 9am".
 */
export function deliveryPromiseGeneric(
  menu: DeliveryMenu,
  option: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>,
): string {
  const business = menu.business_days_only ?? false;
  const by = `by ${hourSpoken(option.ready_hour)}`;

  if (option.days_after === 0) return `the same day as your shoot, ${by}`;
  if (option.days_after === 1) return `the next ${business ? 'business day' : 'day'} after your shoot, ${by}`;

  return `${option.days_after} ${business ? 'business days' : 'days'} after your shoot, ${by}`;
}

/** When it is promised, given when the shoot ends. Null = cannot be kept (a same-day cutoff already past). */
export function deliveryMenuReadyAt(
  menu: DeliveryMenu,
  option: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>,
  shootEndMs: number,
  timezone: string,
): number | null {
  return deliveryRuleReadyAt({ ...option, business_days_only: menu.business_days_only }, shootEndMs, timezone);
}

/**
 * The same, for a rule that carries its own day counting (an order's stamp).
 * Counted from the shoot DAY, in the office's time zone: calendar days unless
 * the rule says business days.
 */
export function deliveryRuleReadyAt(rule: DeliveryRule, shootEndMs: number, timezone: string): number | null {
  const option = rule;
  const business = rule.business_days_only ?? false;
  let day = DateTime.fromMillis(shootEndMs, { zone: timezone });
  const shoot = day;

  for (let i = 0; i < option.days_after; i++) {
    day = day.plus({ days: 1 });

    // Luxon: 6 = Saturday, 7 = Sunday.
    while (business && day.weekday > 5) {
      day = day.plus({ days: 1 });
    }
  }

  const ready = day.set({ hour: option.ready_hour, minute: 0, second: 0, millisecond: 0 });

  return ready > shoot ? ready.toMillis() : null;
}

/**
 * THE SHOOT HAS TO FINISH FIRST (founder, 5 Oct 2026: "hide Same day when
 * the chosen arrival window can't finish in time"). No company records how
 * long a shoot and its editing take, so one rule for every company: a
 * delivery time is offered only when it is promised at least this many
 * hours after the LATEST the photographer can arrive (the end of the
 * arrival window; for "All day", the end of the company's arrival hours).
 * Six hours is a shoot of up to two and editing of about four: a 4 PM
 * arrival cannot keep an 8 PM promise, a noon one can.
 */
export const DELIVERY_FINISH_HOURS = 6;

/**
 * Whether this option's promise leaves the shoot time to finish, for an
 * arrival as late as `latestStart` ("HH:MM", the office's time zone) on
 * `date` (YYYY-MM-DD; any day when it is not known yet — the rule reads the
 * clock, and counting days only ever moves a promise later).
 */
export function deliveryReachable(
  menu: Pick<DeliveryMenu, 'business_days_only'>,
  option: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>,
  shoot: { date?: string | null; latestStart: string },
  timezone: string,
): boolean {
  const day = shoot.date && /^\d{4}-\d{2}-\d{2}$/.test(shoot.date) ? shoot.date : DateTime.now().setZone(timezone).toISODate();
  const start = isoOrNull(`${day}T${shoot.latestStart}`, { zone: timezone });

  if (!start) {
    return true;
  }

  const ready = deliveryRuleReadyAt({ ...option, business_days_only: menu.business_days_only }, start.toMillis(), timezone);

  return ready != null && ready >= start.plus({ hours: DELIVERY_FINISH_HOURS }).toMillis();
}

/**
 * THE PROMISE FOR AN ACTUAL SHOOT (reschedules included): the rule's own
 * time; and when the shoot ends too late for it (a same-day hour already
 * past), the rule's hour on the first later day that leaves the shoot its
 * DELIVERY_FINISH_HOURS — never no promise at all. The customer who paid
 * for the faster time is protected by the late credit; the office is told
 * (deliveryKeptFor, .server/delivery-promise.ts).
 */
export function deliveryPromiseFor(
  rule: DeliveryRule,
  shoot: { startMs: number; endMs: number },
  timezone: string,
  /**
   * The company's CHEAPER options (Standard, in practice). A paid speed the
   * shoot cannot keep is never promised later than one of them would be:
   * paying more must never mean waiting longer (review, 5 Oct 2026 — a Same
   * day shoot ending after 8 PM fell to "next day 8 PM" while Standard was
   * "next day 5 PM").
   */
  cheaper: ReadonlyArray<Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>> = [],
): number {
  const ready = deliveryRuleReadyAt(rule, shoot.endMs, timezone);

  if (ready != null) {
    return ready;
  }

  const earliest = Math.max(shoot.endMs, shoot.startMs + DELIVERY_FINISH_HOURS * 3_600_000);
  let fallback = earliest;

  for (let extra = 1; extra <= 14; extra++) {
    const later = deliveryRuleReadyAt({ ...rule, days_after: rule.days_after + extra }, shoot.endMs, timezone);

    if (later != null && later >= earliest) {
      fallback = later;
      break;
    }
  }

  // Capped at the earliest a cheaper option promises this same shoot.
  const capped = cheaper
    .map((option) => deliveryPromiseFor({ ...option, business_days_only: rule.business_days_only }, shoot, timezone))
    .reduce((min, at) => Math.min(min, at), fallback);

  return capped;
}

/**
 * Whether the rule's own time leaves this actual shoot (its arrival) the
 * DELIVERY_FINISH_HOURS the order form asks of a window — the same test,
 * for a shoot that has a time.
 */
export function deliveryKeptFor(rule: DeliveryRule, shoot: { startMs: number; endMs: number }, timezone: string): boolean {
  const ready = deliveryRuleReadyAt(rule, shoot.endMs, timezone);

  return ready != null && ready >= shoot.startMs + DELIVERY_FINISH_HOURS * 3_600_000;
}

/**
 * The latest arrival that still lets this option be kept, as an hour of the
 * shoot day ("14:00" for an 8 PM same-day promise), or null when any arrival
 * on the shoot day can (a next-day promise).
 */
export function deliveryLatestArrival(option: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>): string | null {
  const hour = option.days_after * 24 + option.ready_hour - DELIVERY_FINISH_HOURS;

  if (hour >= 24) {
    return null;
  }

  return `${String(Math.max(0, hour)).padStart(2, '0')}:00`;
}

/** "Tue, Oct 6, 9:00 AM", in the office's time zone. */
export function deliveryDueLabel(promisedAt: string, timezone: string): string {
  return isoOrNull(promisedAt, { zone: timezone })?.toFormat('ccc, LLL d, h:mm a') ?? '';
}

/** "9 AM", "noon", "11 PM": an hour as it is written on an order. */
function hourWritten(hour: number): string {
  if (hour === 0) return 'midnight';
  if (hour === 12) return 'noon';

  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

/**
 * A promise in words while no shoot is scheduled to count it from: "the day
 * after the shoot, by 9 AM". For the order itself, which the customer, the
 * office and the photographer all read ("your shoot" is the quote's voice,
 * deliveryPromiseGeneric).
 */
export function deliveryRuleWords(rule: DeliveryRule): string {
  const by = `by ${hourWritten(rule.ready_hour)}`;
  const business = rule.business_days_only === true;

  if (rule.days_after === 0) return `the same day as the shoot, ${by}`;
  if (rule.days_after === 1) return `the ${business ? 'next business day' : 'day'} after the shoot, ${by}`;

  return `${rule.days_after} ${business ? 'business days' : 'days'} after the shoot, ${by}`;
}

/**
 * The promise as it reads on an order, to the customer, the office and the
 * photographer: "Next morning (due Tue, Oct 6, 9:00 AM)", or while no shoot
 * is scheduled to count from, "Next morning (the day after the shoot, by
 * 9 AM)".
 */
export function deliveryReleaseLabel(
  release: Pick<DeliveryRelease, 'option_name' | 'promised_at' | 'days_after' | 'ready_hour' | 'business_days_only'>,
  timezone: string,
): string {
  return `${release.option_name} (${
    release.promised_at ? `due ${deliveryDueLabel(release.promised_at, timezone)}` : deliveryRuleWords(release)
  })`;
}

/** A promise the customer paid extra for: the order is a rush for everyone working on it. */
export function deliveryIsRush(release: Pick<DeliveryRelease, 'paid'> | undefined): boolean {
  return Boolean(release) && Number(release!.paid) > 0;
}

/**
 * The options a catalog product can be linked to, as its editor lists them:
 * every option that is not paused, with its fee and its promise.
 */
export function deliveryOptionsForProducts(menu: DeliveryMenu): Array<{ id: string; label: string }> {
  return (menu.options ?? [])
    .filter((option) => option.enabled !== false)
    .map((option) => ({
      id: option.id,
      label: `${option.name}${option.fee > 0 ? ` (+$${option.fee % 1 === 0 ? option.fee.toFixed(0) : option.fee.toFixed(2)})` : ''}: ${deliveryRuleWords(
        { ...option, business_days_only: menu.business_days_only },
      )}`,
    }));
}

/**
 * THE DELIVERY TIME AN OFFERING CARRIES. A company can sell speed as part of
 * an offering ("Express Premier …", priced above its twin) instead of as a
 * line of its own: the catalog product names the menu option it is delivered
 * by (`delivery_option_id`), and an order that books it takes that option.
 *
 * The choice is snapshotted at $0, because the offering's price already
 * carries the speed, with the option's fee remembered as `included`. With
 * several linked products on one order the fastest promise wins. An option
 * that is paused or gone from the menu promises nothing. Read from the
 * stored rows whether or not the menu is `offered`: linking an offering to an
 * option is itself the company selling it.
 */
export function deliveryChoiceFromProducts(
  menu: DeliveryMenu,
  products: ReadonlyArray<{ delivery_option_id?: string | null } | undefined>,
): DeliveryChoice | undefined {
  const linked = new Set(products.map((product) => product?.delivery_option_id).filter((id): id is string => Boolean(id)));

  const fastest = (menu.options ?? [])
    .filter((option) => option.enabled !== false && linked.has(option.id))
    .sort((a, b) => a.days_after - b.days_after || a.ready_hour - b.ready_hour)[0];

  if (!fastest) {
    return undefined;
  }

  return { ...deliveryChoiceOf(fastest, menu), fee: '0.00', included: fastest.fee.toFixed(2) };
}

/** Promised fewer days after the shoot, or earlier on the same day. */
export function deliveryFaster(
  a: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>,
  b: Pick<DeliveryMenuOption, 'days_after' | 'ready_hour'>,
): boolean {
  return a.days_after < b.days_after || (a.days_after === b.days_after && a.ready_hour < b.ready_hour);
}

export type DeliveryOrderChoice = { ok: true; choice: DeliveryChoice | undefined } | { ok: false; error: string };

/**
 * THE DELIVERY TIME AN ORDER IS PLACED WITH: what the customer picked from
 * the company's menu, and what its offerings carry (deliveryChoiceFromProducts).
 * One function for the order form's price, the price check and the intake,
 * so the number on the button is the number on the invoice.
 *
 *   - Nothing picked: what the offerings carry, or nothing (as before).
 *   - Picked, nothing carried: that option, its fee a "Delivery: …" line.
 *   - Picked, and an offering carries a time (MCM's Express): that time is
 *     included. A FASTER pick is an upgrade, charged the difference only, with
 *     what the offering carried remembered as `included`; together they are
 *     what the speed cost, and what a late delivery credits. A pick that is
 *     not faster changes nothing: the included time stands.
 *   - Picked, and not on the menu (paused, gone, or the menu is off): refused,
 *     never quietly swapped for another time.
 */
export function deliveryChoiceForOrder(
  menu: DeliveryMenu,
  products: ReadonlyArray<{ delivery_option_id?: string | null } | undefined>,
  requested?: string | null,
): DeliveryOrderChoice {
  const carried = deliveryChoiceFromProducts(menu, products);

  if (!requested) {
    return { ok: true, choice: carried };
  }

  const option = deliveryMenuFind(menu, requested);

  if (!option) {
    return { ok: false, error: 'That delivery time isn’t offered. Pick another.' };
  }

  if (!carried) {
    return { ok: true, choice: deliveryChoiceOf(option, menu) };
  }

  const included = Number(carried.included ?? 0);

  if (!deliveryFaster(option, { days_after: carried.days_after ?? 0, ready_hour: carried.ready_hour ?? 0 })) {
    return { ok: true, choice: carried };
  }

  const fee = Math.max(0, Math.round((option.fee - included) * 100) / 100);

  return { ok: true, choice: { ...deliveryChoiceOf(option, menu), fee: fee.toFixed(2), included: included.toFixed(2) } };
}

/**
 * The delivery times as the voice agent is told them: marked "required", then
 * each with its id (for set_delivery), its price and its rule, the free one
 * first; then any offering that carries one, so the agent says it is
 * included.
 */
export function deliveryMenuVoiceLine(
  menu: DeliveryMenu,
  carriers: ReadonlyArray<{ id: string; name: string; delivery_option_id: string }> = [],
  /**
   * The order's arrival window, once it is known (the page's own update):
   * a time it cannot finish in time for (deliveryReachable) is left out and
   * said to be. Absent (the session's start, no day yet): every time, each
   * with the latest arrival it can be kept for.
   */
  shoot?: { date?: string | null; latestStart: string; timezone: string; keep?: readonly string[] },
): string {
  const offered = [...deliveryMenuOffered(menu)].sort((a, b) => a.fee - b.fee);
  // Never the cheapest (standard) or one an offering on the order carries (`keep`): those are always on the order form.
  const reachable = (o: DeliveryMenuOption) =>
    !shoot || o === offered[0] || shoot.keep?.includes(o.id) || deliveryReachable(menu, o, shoot, shoot.timezone);
  const options = offered.filter(reachable);
  const dropped = offered.filter((o) => !reachable(o));
  // Taxed speed (DeliveryMenu.speed_taxable): the agent says so with the price.
  const price = (fee: number) => (fee > 0 ? `$${fee % 1 === 0 ? fee.toFixed(0) : fee.toFixed(2)}${menu.speed_taxable ? ' plus tax' : ''}` : 'included at no charge');
  const lines = options.map((o) => {
    const latest = shoot ? null : deliveryLatestArrival(o);
    const limit = latest && o !== options[0] ? ` — only when the arrival window ends by ${hourWritten(Number(latest.slice(0, 2)))} (the shoot has to finish first)` : '';

    return `${o.name} [${o.id}]: ${price(o.fee)}, ${deliveryRuleWords({ ...o, business_days_only: menu.business_days_only })}${limit}`;
  });

  if (dropped.length > 0) {
    lines.push(`not offered for this arrival window, the shoot can't finish in time: ${dropped.map((o) => o.name).join(', ')} — never offer ${dropped.length > 1 ? 'them' : 'it'}`);
  }
  const carried = carriers
    .map((c) => ({ ...c, option: options.find((o) => o.id === c.delivery_option_id) }))
    .filter((c): c is typeof c & { option: DeliveryMenuOption } => Boolean(c.option))
    .map((c) => `${c.name.trim()} [${c.id}] includes ${c.option.name}: with it only a faster time costs extra, and only the difference`);

  // "required": the order form asks this on a step of its own and cannot place
  // the order without an answer, so the agent must ask it too (its DELIVERY
  // section, .server/voice/ordering-agent.ts).
  return `required — ${[...lines, ...carried].join('; ')}`;
}

/** What the Bleenk quote (and anything else that sells) shows: included options first, then cheapest. */
export function deliveryMenuForSale(menu: DeliveryMenu) {
  return deliveryMenuOffered(menu)
    .map((o) => ({
      id: o.id,
      name: o.name,
      fee_cents: Math.round(o.fee * 100),
      promise: deliveryPromiseGeneric(menu, o),
      included: o.fee === 0,
      // Only when the company taxes its delivery speed (DeliveryMenu.speed_taxable).
      ...(menu.speed_taxable && o.fee > 0 ? { taxable: true } : {}),
    }))
    .sort((a, b) => a.fee_cents - b.fee_cents);
}
